import React, { useEffect, useMemo, useRef, useState } from 'react'
import statsBg from '../../../assets/images/homepage/statsbg.webp'

type StatsProps = {
  stats: Array<{ number: string; label: string }>
}

// base on index nuber it decide cycle 5 anmd 3 ( index = 0 (5) & index = 1 (3) & .....) 
function getCycles(digitIndex: number): number {
  return digitIndex % 2 === 0 ? 5 : 3
}

function parseNumber(numStr: string) {
  const digits = numStr.replace(/\D/g, '')
  const suffix = numStr.replace(/[0-9]/g, '')
  return { digits, suffix }
}

// A single slot-reel digit column
type DigitReelProps = {
  targetDigit: number   // 0–9
  digitIndex: number    // position in the number (0 = leftmost)
  progress: number      // 0 → 1, eased globally
  cellHeight: number
}

const DigitReel = ({ targetDigit, digitIndex, progress, cellHeight }: DigitReelProps) => {
  const cycles = getCycles(digitIndex)
  // Total cells needed: cycles full rotations + (targetDigit + 1) extra cells
  const totalCells = cycles * 10 + targetDigit + 1
  const totalScroll = (cycles * 10 + targetDigit) * cellHeight
  const translateY = -(progress * totalScroll)

  // Build the digit cells
  const cells = Array.from({ length: totalCells }, (_, i) => i % 10)

  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        height: cellHeight,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          transform: `translateY(${translateY}px)`,
          willChange: 'transform',
        }}
      >
        {cells.map((digit, i) => (
          <div
            key={i}
            style={{
              height: cellHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {digit}
          </div>
        ))}
      </div>
    </div>
  )
}

const DURATION = 2500 // ms — all digits animate together

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

const Stats = ({ stats }: StatsProps) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [progress, setProgress] = useState(0)   // single shared eased progress 0→1

  const parsedStats = useMemo(() => stats.map((s) => parseNumber(s.number)), [stats])

  // Observe visibility
  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.35 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Run animation when visible
  useEffect(() => {
    if (!isVisible) {
      setProgress(0)
      return
    }

    const startTime = performance.now()
    let frameId = 0

    const tick = (now: number) => {
      const elapsed = now - startTime
      const raw = Math.min(elapsed / DURATION, 1)
      const eased = easeOutCubic(raw)
      setProgress(eased)
      if (raw < 1) {
        frameId = requestAnimationFrame(tick)
      }
    }

    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [isVisible])

  // Derive cell height from a responsive base — adjust to match your Tailwind font sizes
  // lg breakpoint ≈ 40px (text-4xl), xl ≈ 48px, etc. Using a CSS custom property is cleanest,
  // but a fixed 1em-based approach works fine when font-size drives layout.
  const CELL_HEIGHT = 56 // px — tune this to match your rendered font line-height

  return (
    <div
      ref={sectionRef}
      className="
        relative w-full
        h-[100px]  md:h-[100px] lg:h-[150px] xl:h-[170px] 2xl:h-[200px]
        flex items-center justify-center
        overflow-hidden
      "
    >
      <img
        src={statsBg}
        alt="stats background"
        className="absolute inset-0 w-full h-full object-cover"
      />

      <div className="relative z-10 w-full px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32">
        <div className="grid grid-cols-4 gap-2 sm:gap-4 md:gap-8 lg:gap-12 xl:gap-16 2xl:gap-20 w-full">
          {parsedStats.map(({ digits, suffix }, si) => (
            <div
              key={si}
              className="text-center"
            >
              {/* Number row */}
              <div
                className="
                  flex items-center justify-center
                  font-gilroy-bold text-white
                  text-2xl md:text-4xl lg:text-4xl xl:text-5xl 2xl:text-6xl
                  leading-none -mt-4
                "
                style={{ height: CELL_HEIGHT }}
              >
                {digits.split('').map((d, di) => (
                  <DigitReel
                    key={di}
                    targetDigit={parseInt(d)}
                    digitIndex={di}
                    progress={progress}
                    cellHeight={CELL_HEIGHT}
                  />
                ))}
                {suffix && <span style={{ lineHeight: `${CELL_HEIGHT}px` }}>{suffix}</span>}
              </div>

              {/* Label */}
              <p className="font-gilroy font-[500] text-white text-xs md:text-base lg:text-base xl:text-xl 2xl:text-2xl leading-snug -mt-2 md:mb-3 lg:mt-1">
                {stats[si].label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Stats